import {
  faBatteryFull,
  faSignal,
  faWifi,
} from '@fortawesome/pro-solid-svg-icons';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { useTheme } from 'src/components/muiTheme/utils';
import { useMediaQuery } from 'src/components/muiTheme/utils';

export interface PhoneFramePreset {
  id: string;
  name: string;
  width: number;
  height: number;
}

interface PhoneFrameProps {
  children: React.ReactNode;
  fullScreen?: boolean;
  devicePreset?: Pick<PhoneFramePreset, 'width' | 'height'>;
  scale?: number;
}

interface StatusBarProps {
  overlay?: boolean;
}

export const StatusBar = ({ overlay = false }: StatusBarProps) => {
  return (
    <Box
      sx={{
        height: '32px',
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: overlay ? 'white' : 'text.primary',
        fontSize: '12px',
        fontWeight: 'medium',
        ...(overlay && {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '48px',
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 100%)',
            zIndex: 0,
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          },
        }),
      }}
    >
      <Box>9:41</Box>
      <Stack direction="row" spacing={0.5}>
        <Icon icon={faSignal} size="xsmall" />
        <Icon icon={faWifi} size="xsmall" />
        <Icon icon={faBatteryFull} size="xsmall" />
      </Stack>
    </Box>
  );
};

export const PhoneFrame = ({
  children,
  fullScreen = false,
  devicePreset,
  scale = 1,
}: PhoneFrameProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const previewScale = Math.max(scale, 0.1);
  const previewFrameWidth = devicePreset?.width ?? null;
  const previewFrameHeight = devicePreset?.height ?? null;

  if (isMobile) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: previewFrameHeight ? 'flex-start' : 'center',
        ...(previewFrameHeight
          ? {
              flexShrink: 0,
              p: 0,
            }
          : {
              height: '100%',
              maxHeight: '700px',
              p: 2,
            }),
      }}
    >
      {/* iPhone Frame */}
      <Box
        sx={{
          ...(previewFrameHeight
            ? {
                transform: `scale(${previewScale})`,
                transformOrigin: 'top center',
              }
            : {}),
        }}
      >
        <Box
          sx={{
            width: `${previewFrameWidth || 320}px`,
            ...(previewFrameHeight
              ? {
                  height: `${previewFrameHeight}px`,
                }
              : {
                  height: '100%',
                  minHeight: '600px',
                  maxHeight: '640px',
                }),
            borderRadius: '45px',
            position: 'relative',
            backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f8f8',
            boxShadow: `
              0 0 0 2px ${isDarkMode ? '#2a2a2a' : '#e2e2e2'},
              0 20px 40px -10px rgba(0,0,0,0.2)
            `,
            padding: '10px',
            overflow: 'hidden',
          }}
        >
          {/* Notch */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '35%',
              height: '22px',
              backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f8f8',
              borderBottomLeftRadius: '18px',
              borderBottomRightRadius: '18px',
              zIndex: 2,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '25%',
                height: '3px',
                backgroundColor: isDarkMode ? '#2a2a2a' : '#e2e2e2',
                borderRadius: '2px',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '7px',
                right: '25%',
                width: '6px',
                height: '6px',
                backgroundColor: isDarkMode ? '#2a2a2a' : '#e2e2e2',
                borderRadius: '50%',
              },
            }}
          />

          {/* Status Bar */}
          {!fullScreen && <StatusBar />}

          {/* Content */}
          <Box
            sx={{
              height: fullScreen
                ? 'calc(100% - 20px)'
                : 'calc(100% - 32px - 16px)', // Account for frame padding in fullscreen
              width: '100%',
              mt: fullScreen ? 0 : 2, // Add gap for camera area if not fullScreen
              overflow: 'hidden',
              borderRadius: '36px', // Slightly less than frame to prevent content bleed
            }}
          >
            {children}
          </Box>

          {/* Home Indicator */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '120px',
              height: '4px',
              backgroundColor: isDarkMode ? '#404040' : '#cccccc',
              borderRadius: '100px',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
